// Recruiter auth helpers (MVP 2.0 #2).
//
// Workspace-global model: every authenticated recruiter sees all data in the
// deploy. This file is the FE counterpart of `backend/api/auth.py` — thin
// fetch wrappers + a TanStack-Query-backed `useSession()` hook so all
// components share one cached session lookup.
//
// Cookie shape:
//   HttpOnly + Secure + SameSite=Lax cookie named `interview_session` (per
//   `backend/auth/backend.py`). The browser sends it automatically on every
//   request when `credentials: "include"` is set. JS cannot read it, so we
//   infer "logged in?" by polling `/api/auth/me`.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import type { CompletionNotificationMode } from "@/lib/completionNotifications";
import type { TrialInfo } from "@/lib/trial";

export interface RecruiterSession {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  // FEAT-23: the person's own phone number and role in the company. Both
  // optional (null = not given); edited on the Profile page. `job_title` is
  // their JOB — the permission role is `role` below.
  phone?: string | null;
  job_title?: string | null;
  // MVP 3.0 multi-tenancy: the admin UI labels the session by company name
  // (greeting + sidebar). Populated by GET /api/auth/me (joins Company).
  company_name: string;
  // FEAT-2: passed to Paddle Checkout as `custom_data.company_id`, which is how
  // the first subscription webhook finds the tenant. Nothing else reads it.
  company_id: string;
  // The team role (FEAT-28 / FEAT-39): owner · member ("Recruiter") ·
  // external ("External recruiter / Sourcer") · hiring_manager. ⚠ Do not
  // branch on it — ask `useCan` (lib/roles.ts), which reads `permissions`.
  role: "owner" | "member" | "external" | "hiring_manager";
  // FEAT-28 + FEAT-39: what this person may DO here — the role's capabilities
  // plus the boxes the owner ticked for it, resolved by the server
  // (`backend/permissions.py` + the company's matrix). Optional so a session
  // cached before the field existed falls back to the default (`lib/roles.ts`).
  permissions?: string[];
  // MVP 4.0 platform-operator tier. Orthogonal to `role`: grants cross-tenant
  // back-office access. "support" = customer-service surfaces (Recovery +
  // problem-reports); "superuser" = everything incl. operator management;
  // "viewer" (OPS-29) = everything a superuser READS, nothing to change — every
  // write answers 403 server-side and every action control is absent. Gates
  // the back-office route guards + NAV. `is_superuser` mirrors
  // (platform_role === "superuser") — prefer reading platform_role.
  platform_role: "none" | "support" | "superuser" | "viewer";
  // MVP 4.0 item 7: whether this company may use the Traffit ATS integration
  // (resale gate — TRAFFIT_ENABLED_COMPANY_IDS server-side). Hides the Traffit
  // tile + the "Traffit" nav item + guards the /admin/integrations/traffit route
  // for tenants we didn't sell it to. Mirrors the backend `require_traffit_enabled`.
  ats_enabled: boolean;
  // FEAT-47: both doors of the MCP connector hold for this company (the
  // global flag AND the operator's switch) — the "Connected AI assistants"
  // card on Settings → Integrations renders only on true. Optional: a
  // session cached before the field existed reads false.
  mcp_connector_enabled?: boolean;
  // EF-3: this company may switch identity verification on per test right
  // now (the operator's company switch AND the server being able to run
  // checks). Optional: a session cached before the field existed reads false.
  identity_verification_enabled?: boolean;
  // MVP 4.0 optional email 2FA: whether THIS recruiter has 2FA turned on (drives
  // the Profile "Two-factor authentication" card). `company_requires_2fa` is the
  // per-workspace requirement (owner-set) — when true the user can't turn their
  // own 2FA off, and the Profile card shows "required by your workspace".
  two_factor_enabled: boolean;
  company_requires_2fa: boolean;
  // FEAT-36: an authenticator app is enrolled on this account (the sign-in
  // code step then asks for ITS code, email as the fallback) and when it was
  // added. Optional: a session cached before the field existed reads "none".
  totp_enabled?: boolean;
  totp_enabled_at?: string | null;
  // FEAT-37 (Sheet #189): the how-to video's address when the operator has set
  // one on this server (`HELP_VIDEO_URL`). Null or absent = no video yet, and
  // the Help-center card, the account-menu entry and the Getting-started line
  // all stay hidden. Opens in a new tab — never embedded (the CSP stays).
  help_video_url?: string | null;
  // FEAT-30: whether this account has a password at all. FALSE for an account
  // created through a sign-in provider — Profile then offers "email me a link
  // to set a password" and confirms turning 2FA off with an emailed code.
  // Optional: a session cached before the field existed reads "has one".
  has_password?: boolean;
  // FEAT-30: the sign-in providers this person has connected (e.g. ["google"]).
  sign_in_providers?: string[];
  // FEAT-6 (MVP 5.5): the workspace switch for the AI test-builder sidebar on
  // /admin/new. Owner-set in Settings → AI, read from the company row by
  // /api/auth/me so EVERY member (not only the owner, who alone can read
  // /company-settings) knows whether to render it. False hides the sidebar,
  // its rail and its opener button; the /admin/new/ai composer is unaffected.
  ai_suggestions_enabled: boolean;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  email_verified_at: string | null;
  last_login_at: string | null;
  // MVP 4.5 first-run onboarding. The guided tour is eligible only while BOTH
  // tour timestamps are null; the getting-started checklist hides once the
  // dismissed one is set (or once every task is genuinely done). Server-side
  // rather than localStorage because this is per-PERSON state — someone who
  // took the tour on a laptop shouldn't be walked through it again on a phone.
  onboarding_tour_completed_at: string | null;
  onboarding_tour_skipped_at: string | null;
  onboarding_checklist_dismissed_at: string | null;
  // Ids of the in-context guides this person has already been shown (the short
  // walkthroughs on /admin/new/ai and /admin/templates). A SET, not more flags —
  // guides are page-scoped and we expect to keep adding them. Always an array,
  // never null: `useGuide` runs a guide when its id is absent from this list.
  onboarding_guides_seen: string[];
  // EP-18: the owner limited this member to selected recruitments. Only the
  // flag — the server already filters every list, so the UI needs it just to
  // explain why the workspace looks smaller and to disable job authoring.
  // Optional so a session cached before the field existed reads "not limited".
  job_access_restricted?: boolean;
  // FEAT-2 billing. `billing_enabled` is the server's dark flag: while it is
  // false the other fields describe today's product (full access, no seat
  // limit) and nothing billing-related renders. These MIRROR the server gates
  // so the UI can hide what it would be refused — they never replace them.
  billing_enabled: boolean;
  plan: string;
  plan_status: string;
  ai_review_enabled: boolean;
  // Recruiter seats (owner · admin · recruiter) and, since the pricing Sheet
  // (2026-10-06), the separate hiring-manager pool. null = unlimited.
  seat_limit: number | null;
  manager_seat_limit?: number | null;
  // The job-draft budget of a counted plan — a running trial, or Core this
  // calendar month: "AI job drafts: 2 of 3 left". Null for every other plan.
  ai_jd_drafts?: { used: number; limit: number } | null;
  // The one cohort the plan wall is for: created after the go-live cutover with
  // no paid plan. NEVER true for a grandfathered workspace.
  needs_plan: boolean;
  // The package matrix resolved for this company — `{feature_key: allowed}`
  // for every feature the pricing PDF sells, computed by the same server
  // function every gate calls. Read it through `usePlanAccess`, never
  // directly; `{}` from a server that predates the field means "open".
  feature_access: Record<string, boolean>;
  // F5b: the matrix's tiers, `{feature_key: "Basic" | "Core" | "Pro"}` — the
  // server's `FEATURE_TIERS` itself, so "Upgrade to Core" names the tier the
  // server would refuse at. Read through `usePlanAccess().tierFor`, which
  // falls back to the browser's mirror for a session cached before the field.
  feature_tiers?: Record<string, string>;
  // Build B: what THIS SERVER can do, as opposed to what the plan grants —
  // `camera_snapshots` / `file_upload_questions` are false until the platform
  // can store candidate files (the flag, a bucket, and on prod legal v1.6).
  // Optional: a server that predates the field reads as "not available".
  platform_features?: Record<string, boolean>;
  // FEAT-12: the coding languages this server can author (registry order) —
  // the browser ones always, the code-runner ones while the runner lists them.
  code_languages?: string[];
  // FEAT-2e: the workspace's free trial when one is IN FORCE — "active" with
  // `days_left`, or "ended" for a company walled because it ran out. Null for
  // everyone else (grandfathered, paid, overridden, billing dark). Drives the
  // two one-time notices (`TrialNotice`) and the account-menu plan line.
  trial: TrialInfo | null;
}

// FEAT-36: what the second step asks for — the authenticator app's code
// ("totp"; `requestEmailCodeInstead` swaps it for an emailed one) or a code
// that is already on its way by email.
export type SecondFactorMethod = "totp" | "email";

// Result of a password step. `ok` → session cookie set (fully logged in). A
// `2fa_required` login means the password was correct but a 6-digit code —
// from the app, or emailed — must now be submitted at /api/auth/login/2fa
// before a session is issued.
//
// `emailed` (OPS-28 #5): for an emailed code, whether the mail was handed to
// the provider. False = it was not, and the step must say so and offer the
// resend — not "we emailed you a code". Always true for the app's code.
export type LoginResult =
  | { status: "ok" }
  | {
      status: "2fa_required";
      challengeId: string;
      method: SecondFactorMethod;
      emailed: boolean;
    };

// Exported so the admin fetch layer (api.ts) can null the cached session on a
// 401 — see adminFetch. Keep in sync with the useSession queryKey below.
export const SESSION_QUERY_KEY = ["auth", "session"] as const;

/**
 * The session as the cache holds it RIGHT NOW — what the two login doors
 * read after awaiting the SESSION_QUERY_KEY invalidation, so the post-login
 * target can tell a first run (SIMP-37). Null when nothing landed, which
 * every caller treats as "not a first run": a slow /me can never misroute.
 */
export function cachedSession(queryClient: QueryClient): RecruiterSession | null {
  return queryClient.getQueryData<RecruiterSession | null>(SESSION_QUERY_KEY) ?? null;
}

/**
 * Lay a partial session (what PATCH /me/onboarding echoes, or an optimistic
 * guess at it) over the cached one — a no-op while nothing is cached, so a
 * write that lands after sign-out cannot resurrect a session. The four
 * onboarding writers each spelled this updater by hand (SIMP-6).
 */
export function mergeCachedSession(
  queryClient: QueryClient,
  next: Partial<RecruiterSession>,
): void {
  queryClient.setQueryData(
    SESSION_QUERY_KEY,
    (prev: RecruiterSession | null | undefined) =>
      prev ? { ...prev, ...next } : prev,
  );
}

// `credentials: "include"` is what makes the session cookie flow. Use this
// for every auth endpoint AND every admin endpoint that should see the
// session (the four MVP 2.0 #2 session-aware aggregates).
async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  return fetch(input, {
    ...init,
    credentials: "include",
  });
}

// ---------- Read: current session ------------------------------------------

export async function getSession(): Promise<RecruiterSession | null> {
  const res = await authFetch("/api/auth/me");
  if (res.status === 401) return null;
  if (!res.ok) {
    // 5xx / network: treat as "session unknown" — fail closed (UI redirects
    // to login rather than rendering as if logged in).
    return null;
  }
  return (await res.json()) as RecruiterSession;
}

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: getSession,
    // 5 min. Refetched on login/logout mutations (which invalidate this key);
    // window-refocus refetch is OFF globally (queryClient.ts). A removed/
    // logged-out session is also caught eagerly by adminFetch's 401 handler,
    // which nulls this cache so the gates bounce to /login immediately.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

// ---------- Mutations -------------------------------------------------------

export async function login(
  email: string,
  password: string,
  rememberMe: boolean,
): Promise<LoginResult> {
  // fastapi-users uses OAuth2PasswordRequestForm — form-encoded, field name
  // `username` (we pass the email there per the standard convention).
  // `remember_me` is a custom Form field on our wrapped /login route; when
  // truthy the server sets cookie Max-Age = full TTL, otherwise it omits
  // Max-Age (browser session cookie that dies on close).
  const body = new URLSearchParams();
  body.set("username", email);
  body.set("password", password);
  body.set("remember_me", rememberMe ? "true" : "false");
  const res = await authFetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (res.status === 401) throw new AuthError("LOGIN_BAD_CREDENTIALS", 401);
  // A 403 is one of several distinct cases — unverified email, or the company
  // being suspended / pending-approval / declined (MVP 4.0 admission control).
  // Carry the real detail code so the page can render the right message + decide
  // whether to offer "resend verification".
  if (res.status === 403) throw new AuthError(await readDetail(res), 403);
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  // 204 → cookie set, fully logged in. 200 → 2FA challenge: the JSON body carries
  // the opaque challenge_id to submit alongside the emailed code.
  if (res.status === 200) {
    const data = (await res.json().catch(() => null)) as
      | { status?: string; challenge_id?: string; method?: string; emailed?: boolean }
      | null;
    if (data?.status === "2fa_required" && data.challenge_id) {
      return {
        status: "2fa_required",
        challengeId: data.challenge_id,
        // A server that predates FEAT-36 says nothing: that was always email.
        method: data.method === "totp" ? "totp" : "email",
        // Absent (an older server) means sent, exactly as before.
        emailed: data.emailed !== false,
      };
    }
    // OPS-28 #30: a 200 that is not a challenge (an unparseable or unexpected
    // body — a proxy page, a half-deployed server) is NOT a login. Reading it
    // as one set no cookie, so the next guard bounced back to /login with no
    // message at all. Say so instead.
    throw new AuthError("LOGIN_UNEXPECTED_RESPONSE", 200);
  }
  return { status: "ok" };
}

// FEAT-36 — "Email me a code instead" on the code step: voids the app challenge
// and returns the emailed one's id (same remember-me). Also the "Resend" after
// that swap, since re-running the password login would hand back an app
// challenge. Throws AuthError "CHALLENGE_INVALID" (410) once the step is over.
export async function requestEmailCodeInstead(
  challengeId: string,
): Promise<{ challengeId: string; emailed: boolean }> {
  const res = await authFetch("/api/auth/login/2fa/email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge_id: challengeId }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json()) as { challenge_id: string; emailed?: boolean };
  // OPS-28 #5: the challenge exists even when the mail did not go out.
  return { challengeId: data.challenge_id, emailed: data.emailed !== false };
}

// Submit the emailed 6-digit code for a `2fa_required` login. On success the
// server sets the session cookie (+ a 30-day trusted-device cookie when
// `trustDevice`), so the caller invalidates SESSION_QUERY_KEY afterwards. Throws
// AuthError with detail "BAD_CODE" (wrong code), "CHALLENGE_INVALID" (expired/
// used) or "TOO_MANY_ATTEMPTS" (cap hit — restart the login).
export async function verifyLoginCode(
  challengeId: string,
  code: string,
  trustDevice: boolean,
): Promise<void> {
  const res = await authFetch("/api/auth/login/2fa", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      challenge_id: challengeId,
      code,
      trust_device: trustDevice,
    }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

export async function logout(): Promise<void> {
  await authFetch("/api/auth/logout", { method: "POST" });
}

export interface SignupBody {
  // FEAT-48 (Leo, 2026-10-21): the four fields the form sends — and no
  // password: the owner is created without one and signs in through the
  // emailed link (`exchangeSignInLink`); Profile sets a password later.
  company_name: string;
  email: string;
  first_name: string;
  last_name: string;
  // FEAT-23: optional, off the form since FEAT-48 (FEAT-45 had required it
  // for a day); validated only when given. Leave the key out for "not given".
  phone?: string;
  // FEAT-23: optional, and no longer on the form. Leave the key out for
  // "not given".
  job_title?: string;
  // CP-2: the company's legal identity. FEAT-45 took it OFF the sign-up form —
  // it is asked for on the plan wall before the first checkout, and in
  // Settings › Branding — so these are optional here and the page never sends
  // them. An API client that does must send the whole address.
  // `address_country` is an ISO 3166-1 alpha-2 CODE from `lib/countries.ts`,
  // never a country name — see that file for why.
  tax_number?: string;
  address_street?: string;
  address_city?: string;
  address_postal_code?: string;
  address_country?: string;
  // FEAT-41: the signed handoff from the landing page's job-offer builder
  // (`lib/landingDraft.ts`). Optional; the server builds the carried test in
  // the new workspace and ignores a token it did not sign.
  landing_draft?: string;
  // FEAT-48 "Wrong address?": the previous answer's `retryToken`, sent with the
  // SAME body and the corrected email — the server moves the address onto the
  // owner that sign-up created instead of creating a second workspace.
  retry_token?: string;
}

// Open self-serve signup (MVP 3.0 team model): creates a NEW company workspace
// + its OWNER account in one transaction, then emails a one-time SIGN-IN link
// (FEAT-48) — clicking it verifies the address and signs the owner in. With
// the manual gate on (MVP 4.0 admission control, `SIGNUP_REQUIRES_APPROVAL`)
// the link confirms the email but the session waits for a superuser's approval.
//
// F7 (2026-07-08 security audit): the endpoint now returns a GENERIC 202 for
// both a real signup AND an already-registered email (no account-existence
// oracle) — so there's no recruiter body to read; a 2xx just means "we've taken
// it from here, check your email". A taken COMPANY name still 400s
// (SIGNUP_COMPANY_NAME_TAKEN), which the caller surfaces.
// FEAT-34 verify (2026-10-06): what the platform did with a sign-up — held
// for back-office approval, or admitted with (or without) a free trial. From
// the server's SETTINGS, the same for every address, so the done screen can
// say it without becoming an account oracle.
export interface SignupAdmission {
  requiresApproval: boolean;
  trialStarted: boolean;
  // FEAT-48: what "Wrong address?" sends back (one hour). Opaque — for an
  // address already registered it is a dud of the same shape (F7), which the
  // server answers as an ordinary sign-up. Null from a server without it.
  retryToken: string | null;
}

function readAdmission(data: {
  requires_approval?: boolean;
  trial_started?: boolean;
  retry_token?: string;
}): SignupAdmission {
  return {
    requiresApproval: !!data.requires_approval,
    trialStarted: !!data.trial_started,
    retryToken: typeof data.retry_token === "string" ? data.retry_token : null,
  };
}

export async function signup(body: SignupBody): Promise<SignupAdmission> {
  const res = await authFetch("/api/auth/signup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readAuthError(res);
  return readAdmission(await res.json());
}

// ---------- "Ask to join" (FEAT-50b) ----------------------------------------

// The four sign-up fields, confirmed from the used-domain refusal
// (`UsedDomainNotice`). The server stores one live request per workspace and
// address for 14 days and mails the workspace's owner(s) once; it answers the
// SAME 202 whether or not anything was stored (no oracle beyond the domain
// one the 409 already is), so this throws only on a rate limit (429), a
// personal address (400 — the form could not have reached the 409 with one)
// or a transport failure.
export interface JoinRequestBody {
  company_name: string;
  first_name: string;
  last_name: string;
  email: string;
}

export async function requestToJoin(body: JoinRequestBody): Promise<void> {
  const res = await authFetch("/api/auth/join-request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readAuthError(res);
}

// The click on the link `requestToJoin` mails the person (R3): this, not the
// request above, is what reaches the workspace's owners. 410 = expired or not ours.
export async function confirmJoinRequest(token: string): Promise<void> {
  const res = await authFetch("/api/auth/join-request/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw await readAuthError(res);
}

// ---------- The emailed sign-in link (FEAT-48) ------------------------------

// "Email me a sign-in link" on /login, and "Send it again" on the sign-up's
// done screen. Always 202 (enumeration-safe) — the server mails a link only to
// a live, non-operator account, and throttles the address. Throws only on the
// rate limit (429) or a transport failure.
export async function requestSignInLink(email: string): Promise<void> {
  const res = await authFetch("/api/auth/sign-in-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// What the link's one use answers: the session cookie is set (`signed_in`),
// or a second factor is owed and /login's code step takes it from here — the
// same `2fa_required` shape the password login returns.
export type SignInLinkResult =
  | { status: "signed_in" }
  | {
      status: "2fa_required";
      challengeId: string;
      method: SecondFactorMethod;
      emailed: boolean;
    };

// Consume `/auth/link/<token>`. Throws AuthError 410 "SIGN_IN_LINK_INVALID"
// for a used, expired or unknown link (one answer for all three), a 403 gate
// code (the address IS confirmed; the workspace is pending / suspended /
// declined) or 429 on the per-IP limit.
export async function exchangeSignInLink(token: string): Promise<SignInLinkResult> {
  const res = await authFetch("/api/auth/sign-in-link/exchange", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json().catch(() => null)) as
    | { status?: string; challenge_id?: string; method?: string; emailed?: boolean }
    | null;
  if (data?.status === "2fa_required" && data.challenge_id) {
    return {
      status: "2fa_required",
      challengeId: data.challenge_id,
      method: data.method === "totp" ? "totp" : "email",
      emailed: data.emailed !== false,
    };
  }
  if (data?.status === "signed_in") return { status: "signed_in" };
  // A 200 that is neither (a proxy page, a half-deployed server) is not a
  // sign-in — the same posture as `login`.
  throw new AuthError("LOGIN_UNEXPECTED_RESPONSE", 200);
}

export async function verifyEmail(token: string): Promise<RecruiterSession> {
  const res = await authFetch("/api/auth/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return (await res.json()) as RecruiterSession;
}

export async function resendVerification(email: string): Promise<void> {
  // Always 202 regardless of email — enumeration-safe by design.
  await authFetch("/api/auth/resend-verification", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

export async function forgotPassword(email: string): Promise<void> {
  // Always 202 regardless of email — enumeration-safe by design.
  await authFetch("/api/auth/forgot-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(
  token: string,
  password: string,
): Promise<void> {
  const res = await authFetch("/api/auth/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// ---------- Self-service profile (PATCH /api/admin/me) ----------------------

export interface ProfileUpdateResult {
  first_name: string;
  last_name: string;
  display_name: string;
  phone: string | null;
  job_title: string | null;
}

// Edit the signed-in recruiter's own name, phone number and role in the
// company. Session-guarded server-side; never privilege flags / email /
// password. Callers invalidate SESSION_QUERY_KEY on success so the greeting +
// labels refresh.
//
// `phone` / `job_title` are TRI-STATE on the server: a key that is LEFT OUT
// keeps the stored value, "" (or null) clears it. The Profile form sends one
// only when the person changed it there (lib/profilePatch.ts), so a stale tab
// never wipes a number another tab saved.
export async function updateProfile(body: {
  first_name: string;
  last_name: string;
  phone?: string | null;
  job_title?: string | null;
}): Promise<ProfileUpdateResult> {
  const res = await authFetch("/api/admin/me", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return (await res.json()) as ProfileUpdateResult;
}

// ---------- First-run onboarding (PATCH /api/admin/me/onboarding) -----------

// The four onboarding columns, AS the session carries them — a projection,
// so a field renamed on `RecruiterSession` cannot leave this behind (SIMP-32).
export type OnboardingState = Pick<
  RecruiterSession,
  | "onboarding_tour_completed_at"
  | "onboarding_tour_skipped_at"
  | "onboarding_checklist_dismissed_at"
  | "onboarding_guides_seen"
>;

// Record that the signed-in recruiter finished/skipped the guided tour, hid the
// getting-started checklist, or was shown an in-context guide. Every BOOLEAN
// flag is optional and tri-state: `true` stamps the timestamp, `false` CLEARS
// it — which is how the "Take the product tour" replay entry makes the tour
// eligible again. Sending none is a 422.
//
// `guide_seen` is the exception: a set membership, so it is additive only and
// the server rejects any id not on its allowlist (ONBOARDING_GUIDE_IDS in
// backend/api/admin.py, kept in parity with GUIDE_IDS in tour/guides.ts by
// backend/tests/test_onboarding_state.py).
//
// Callers write the result straight into the session cache with setQueryData
// (same channel as WorkspaceNameCard) rather than invalidating: the response
// carries the authoritative state, so a refetch would only add a round-trip
// during which the tour could flash back on.
export async function updateOnboardingState(body: {
  tour_completed?: boolean;
  tour_skipped?: boolean;
  checklist_dismissed?: boolean;
  guide_seen?: string;
}): Promise<OnboardingState> {
  const res = await authFetch("/api/admin/me/onboarding", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return (await res.json()) as OnboardingState;
}

// ---------- Own notification preferences (/api/admin/me/notifications) ------
//
// EP-9 + EP-10 (MVP 6.0). Lives here, not in api.ts, because every other
// /api/admin/me* call does — same authFetch + readDetail + AuthError shape, so
// `AuthError.message === "UNKNOWN_JOB_IDS"` is the inline-error hook.

export interface MyNotificationSettings {
  completion_notifications_enabled: boolean;
  // EMPTY = every recruitment, including ones added later. A non-empty list =
  // exactly these. There is no third state: "notify me about nothing" is
  // `completion_notifications_enabled: false`.
  job_ids: string[];
  // Read-only CONTEXT. A member cannot open Settings (owner-only), so this is
  // the only way their card can explain the case where their own switch is on
  // and nothing arrives: the workspace mode is "off".
  company_completion_notification_mode: CompletionNotificationMode;
}

export async function fetchMyNotificationSettings(): Promise<MyNotificationSettings> {
  const res = await authFetch("/api/admin/me/notifications");
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return (await res.json()) as MyNotificationSettings;
}

export async function updateMyNotificationSettings(body: {
  completion_notifications_enabled?: boolean;
  job_ids?: string[];
}): Promise<MyNotificationSettings> {
  const res = await authFetch("/api/admin/me/notifications", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return (await res.json()) as MyNotificationSettings;
}

// ---------- Self-service password change (POST /api/auth/change-password) ----

// Change — or, for an account that has none yet, SET — the signed-in
// recruiter's own password. Unlike forgot/reset (which go through an emailed
// token), this is the in-app flow. For an account WITH a password the server
// verifies `current_password`, enforces the same policy as reset on
// `new_password` (≥12 chars, may not contain the email), then invalidates
// every OTHER session while keeping THIS one alive ("changed"). For an
// account WITHOUT one (FEAT-48: a free-trial owner who signed in through the
// emailed link, or a FEAT-30 provider account) `current_password` is left out,
// the code from `setPasswordStart` goes along instead (R4), and nothing is
// evicted ("set") — the server decides from the account. On error,
// AuthError.message carries the backend `detail`: the literal
// "CHANGE_PASSWORD_BAD_CURRENT" when the current password is wrong, the human-
// readable policy reason for an invalid new one, or (a first password) the
// code's "TWO_FACTOR_CODE_REQUIRED" · "BAD_CODE" · "CHALLENGE_INVALID" ·
// "TOO_MANY_ATTEMPTS".
export async function changePassword(body: {
  current_password?: string;
  new_password: string;
  challenge_id?: string;
  code?: string;
}): Promise<"changed" | "set"> {
  const res = await authFetch("/api/auth/change-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json().catch(() => null)) as { status?: string } | null;
  return data?.status === "set" ? "set" : "changed";
}

// FEAT-48 R4 — the emailed code an account with NO password types before its
// first one is set. Refused with "PASSWORD_ALREADY_SET" for an account that has
// one, and 502 "EMAIL_SEND_FAILED" when the code could not be mailed.
export async function setPasswordStart(): Promise<{ challengeId: string }> {
  const res = await authFetch("/api/auth/change-password/start", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json()) as { challenge_id: string };
  return { challengeId: data.challenge_id };
}

// ---------- Team-invite acceptance (MVP 3.0) --------------------------------

export interface InviteInfo {
  company_name: string;
  email: string;
  role: string;
  // Set ("support" | "superuser") when the invitation onboards a platform
  // operator. FEAT-30 reads it: operators stay on password + 2FA, so that
  // invitation never offers a sign-in provider.
  platform_role?: string | null;
}

// Resolve a team-invite token → context for the /accept-invite page. Throws
// AuthError (status 410) when the invite is missing / expired / already used.
export async function fetchInviteInfo(token: string): Promise<InviteInfo> {
  const res = await authFetch(
    `/api/auth/accept-invite?token=${encodeURIComponent(token)}`,
  );
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return (await res.json()) as InviteInfo;
}

// Accept a team invite: creates (or reactivates) the recruiter + sets their
// password. The new account is pre-verified (clicking the link proved email
// ownership), so they can sign in immediately afterward.
export async function acceptInvite(body: {
  token: string;
  password: string;
  first_name: string;
  last_name: string;
}): Promise<void> {
  const res = await authFetch("/api/auth/accept-invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// ---------- Hook helpers (login/logout that invalidate the session cache) --

export function useLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      password: string;
      rememberMe: boolean;
    }) => login(body.email, body.password, body.rememberMe),
    onSuccess: async (result) => {
      // Only a completed login (204 → { status: "ok" }) means we hold a session.
      // A `2fa_required` result leaves the user unauthenticated until they verify
      // the code, so DON'T refetch /me yet (it would just return null).
      if (result.status === "ok") {
        await qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      }
    },
  });
}

// ---------- Two-factor authentication (MVP 4.0) -----------------------------

// Begin enabling 2FA: the server emails a confirmation code (proving the mailbox
// receives codes before we enforce). Returns the challenge id to pair with the code.
export async function enable2faStart(): Promise<{ challengeId: string }> {
  const res = await authFetch("/api/auth/2fa/enable/start", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json()) as { challenge_id: string };
  return { challengeId: data.challenge_id };
}

// Confirm the enable-2FA code → turns 2FA on + trusts this browser. Callers
// invalidate SESSION_QUERY_KEY so the Profile card reflects the new state.
export async function enable2faConfirm(
  challengeId: string,
  code: string,
): Promise<void> {
  const res = await authFetch("/api/auth/2fa/enable/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge_id: challengeId, code }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// Turn 2FA off (re-verifies the current password). Throws AuthError with detail
// "TWO_FACTOR_BAD_PASSWORD" (wrong password), "TWO_FACTOR_REQUIRED_BY_WORKSPACE"
// (403 — the owner requires 2FA, so a member can't opt out) or
// "TWO_FACTOR_REQUIRED_FOR_OPERATOR" (403 — a platform operator always owes it).
export async function disable2fa(currentPassword: string): Promise<void> {
  const res = await authFetch("/api/auth/2fa/disable", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ current_password: currentPassword }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// FEAT-30 — the way through for an account with NO password (it was created
// through a sign-in provider): the server emails a code instead of checking a
// password. Refused with "TWO_FACTOR_PASSWORD_REQUIRED" for an account that has
// one, and with the workspace rule's or the operator role's 403 like `disable2fa`.
export async function disable2faStart(): Promise<{ challengeId: string }> {
  const res = await authFetch("/api/auth/2fa/disable/start", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json()) as { challenge_id: string };
  return { challengeId: data.challenge_id };
}

// …and its second half. Same error codes as the enable confirmation
// ("BAD_CODE" · "CHALLENGE_INVALID" · "TOO_MANY_ATTEMPTS").
export async function disable2faWithCode(
  challengeId: string,
  code: string,
): Promise<void> {
  const res = await authFetch("/api/auth/2fa/disable", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge_id: challengeId, code }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// ---------- FEAT-36: the authenticator app -----------------------------------

// Begin adding an app: a fresh secret (to type) and the otpauth URI (to scan as
// a QR). Nothing changes until `totpConfirm` — an app that already works keeps
// working, so a re-enrol that stops halfway costs nothing.
export async function totpStart(): Promise<{ secret: string; otpauthUri: string }> {
  const res = await authFetch("/api/auth/2fa/totp/start", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json()) as { secret: string; otpauth_uri: string };
  return { secret: data.secret, otpauthUri: data.otpauth_uri };
}

// What adding an app re-verifies (Leo, 2026-10-10) — the proof Remove asks:
// the current password, or for a passwordless account the code `totpAddStart`
// emailed. The server decides from the account which one counts.
export type TotpAddProof =
  | { currentPassword: string }
  | { challengeId: string; code: string };

// The first code from the app, plus the proof, makes it the second factor; 2FA
// is on from here and this browser is trusted. "BAD_CODE" (the APP's code)
// leaves the QR valid for another try; "TOTP_NOT_PENDING" means start was never
// called (or the page reloaded). The proof's own refusals are Remove's —
// "TWO_FACTOR_BAD_PASSWORD" · "TWO_FACTOR_CODE_REQUIRED" — plus, for the emailed
// code, "TWO_FACTOR_BAD_EMAIL_CODE" · "CHALLENGE_INVALID" · "TOO_MANY_ATTEMPTS".
export async function totpConfirm(code: string, proof: TotpAddProof): Promise<void> {
  const res = await authFetch("/api/auth/2fa/totp/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      code,
      proof:
        "currentPassword" in proof
          ? { current_password: proof.currentPassword }
          : { challenge_id: proof.challengeId, code: proof.code },
    }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// A passwordless account proves itself with an emailed code before adding an
// app — `totpRemoveStart`'s twin. Refused with "TWO_FACTOR_PASSWORD_REQUIRED"
// for an account that has a password, "TOTP_ALREADY_ENABLED" while one is live.
export async function totpAddStart(): Promise<{ challengeId: string }> {
  const res = await authFetch("/api/auth/2fa/totp/add/start", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json()) as { challenge_id: string };
  return { challengeId: data.challenge_id };
}

// Remove the app (the current password). 2FA stays on — codes come by email
// again. "TWO_FACTOR_BAD_PASSWORD" · "TOTP_NOT_ENABLED".
export async function totpRemove(currentPassword: string): Promise<void> {
  const res = await authFetch("/api/auth/2fa/totp/remove", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ current_password: currentPassword }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// A passwordless account removes the app with an emailed code instead — the
// same two halves as `disable2faStart` / `disable2faWithCode`.
export async function totpRemoveStart(): Promise<{ challengeId: string }> {
  const res = await authFetch("/api/auth/2fa/totp/remove/start", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  const data = (await res.json()) as { challenge_id: string };
  return { challengeId: data.challenge_id };
}

export async function totpRemoveWithCode(
  challengeId: string,
  code: string,
): Promise<void> {
  const res = await authFetch("/api/auth/2fa/totp/remove", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge_id: challengeId, code }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// ---------- Sign-in providers — "Continue with Google" (FEAT-30) -----------
//
// The round trip itself is a full-page form POST + redirects (see
// lib/signInProviders.ts); these are the few XHR calls around it.

// Which providers the logged-out pages may offer. Empty while the server
// switch is off — and on any failure, so a hiccup hides the button rather than
// drawing one that leads nowhere.
export async function fetchSignInProviders(): Promise<string[]> {
  try {
    const res = await authFetch("/api/auth/oauth/providers");
    if (!res.ok) return [];
    const data = (await res.json()) as { providers?: unknown };
    return Array.isArray(data.providers)
      ? data.providers.filter((p): p is string => typeof p === "string")
      : [];
  } catch {
    return [];
  }
}

export function useSignInProviders() {
  return useQuery({
    queryKey: ["auth", "sign-in-providers"],
    queryFn: fetchSignInProviders,
    // It cannot change without a machine restart, and the endpoint is
    // `no-store`, so one request per tab is the whole cost.
    staleTime: Infinity,
    retry: false,
  });
}

export interface PendingSignIn {
  // "login" = the usual emailed 2FA code; "link" = confirming a provider
  // account; "totp" (FEAT-36) = the authenticator app's code, nothing mailed.
  purpose: "login" | "link" | "totp";
  provider: string;
  email: string;
  // OPS-28 #5: whether the step's code mail was handed to the provider — on
  // every answer since the 2026-10-25 SSO check (the step's cookie carries
  // it); true on an app step. Absent from an older server: read as sent.
  emailed?: boolean;
}

// What the code step after a provider sign-in is for, or null when there is
// none (the cookie that carries it lives ten minutes).
export async function fetchPendingSignIn(): Promise<PendingSignIn | null> {
  const res = await authFetch("/api/auth/oauth/pending");
  if (!res.ok) return null;
  return (await res.json()) as PendingSignIn;
}

// FEAT-36 — `requestEmailCodeInstead` for the provider flow: the challenge id
// rides the HttpOnly cookie, which comes back re-sealed as an emailed-code
// step ("login"). Throws AuthError "CHALLENGE_INVALID" (410) once the step is
// over — the cookie is cleared with it.
export async function requestPendingEmailCode(): Promise<PendingSignIn> {
  const res = await authFetch("/api/auth/oauth/pending/email", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return (await res.json()) as PendingSignIn;
}

// /login/2fa's twin for a provider sign-in: the challenge id rides an HttpOnly
// cookie, so only the code is sent. Same error codes as `verifyLoginCode`.
// Resolves null once signed in (204), or with the NEXT step (202) when a
// `link` code connected the account and the 2FA rule still owes a second
// factor (S2, pre-ship 2026-10-10) — the cookie already carries that step.
export async function confirmSignInCode(
  code: string,
  trustDevice: boolean,
): Promise<PendingSignIn | null> {
  const res = await authFetch("/api/auth/oauth/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, trust_device: trustDevice }),
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  return res.status === 202 ? ((await res.json()) as PendingSignIn) : null;
}


// Forget every remembered device (e.g. a lost laptop): each will be challenged
// for a code again at next login. 2FA itself stays enabled.
export async function forgetTrustedDevices(): Promise<void> {
  const res = await authFetch("/api/auth/2fa/trusted-devices/forget-all", {
    method: "POST",
  });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
}

// ---------- DEV ONLY: one-click local sign-in -------------------------------

// Local-only convenience: signs in as the seeded bootstrap recruiter
// (`dev@example.com`) with NO password, via POST /api/auth/dev-login. The
// backend route 404s in production, and the only caller (the Login page button)
// renders solely under `import.meta.env.DEV`, so this never ships to prod.
export async function devLogin(): Promise<void> {
  const res = await authFetch("/api/auth/dev-login", { method: "POST" });
  if (!res.ok) throw new AuthError(await readDetail(res), res.status);
  // Server sets the session cookie; no body to consume.
}

export function useDevLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: devLogin,
    onSuccess: async () => {
      // Same as a normal login: force /me to repopulate so the gates pass.
      await qc.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });
}

export function useLogoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      // Cancel in-flight fetches so they don't 401-spam the console as the
      // session drops (QA-006), then mark the session logged-out by SETTING it
      // to null. Setting it (vs removeQueries-ing it) keeps the gate's
      // useSession observer attached, so RequireAuth re-renders and redirects
      // to /login immediately.
      //
      // ⚠️ Do NOT call a blanket `qc.removeQueries()` here. Removing the session
      // query DETACHES the gate's observer WITHOUT re-rendering it, so the tree
      // froze in its logged-in state and the `setQueryData(null)` below had no
      // observer left to notify — sign-out appeared to do nothing until the next
      // navigation (the "Sign out doesn't work / Back office signs me out" bug).
      await qc.cancelQueries();
      qc.setQueryData(SESSION_QUERY_KEY, null);
      // Drop cached workspace data — but NOT the session key — so a different
      // account signing in on this shared device can't momentarily see the
      // previous tenant's tests/questions. Excluding the session key is what
      // keeps the gate observer alive (see above).
      qc.removeQueries({
        predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0],
      });
    },
  });
}

// ---------- Errors ----------------------------------------------------------

export class AuthError extends Error {
  status: number;
  // The request field the server blamed, when it said so — `detail.field` on a
  // business 400, or the `loc` tail of a 422. Undefined when the error isn't
  // about one field. The sign-up form points its banner at it.
  readonly field?: string;
  // The server's whole `detail` object when it sent one (FEAT-50a: the
  // used-domain 409 carries the workspace's name and its two ways out beside
  // `code` / `reason` / `field`). Undefined for a string or a 422 list.
  readonly detail?: Record<string, unknown>;
  constructor(
    detail: string,
    status: number,
    field?: string,
    payload?: Record<string, unknown>,
  ) {
    super(detail);
    this.name = "AuthError";
    this.status = status;
    this.field = field;
    this.detail = payload;
  }
}

/** A FastAPI 422 entry. `loc` is ["body", "<field>"], or just ["body"] when a
 *  model-level validator raised — which is why the backend refuses business
 *  rules in the ROUTE instead (see `_signup_field_error`). */
interface ValidationEntry {
  loc?: unknown[];
  msg?: string;
  type?: string;
}

/**
 * Turn a parsed error body into a message + the field it blames.
 *
 * Three shapes reach here, and until EP-2 only the first two were understood:
 * a plain string `detail`; an object `{code, field?, reason?}`; and a 422
 * ARRAY. The array fell through to the literal "HTTP 422" — an array passes
 * `typeof === "object"` but has no `.reason`/`.code` — so every Pydantic
 * refusal on every auth call was invisible to the user.
 */
export function parseErrorBody(detail: unknown, status: number): {
  message: string;
  field?: string;
} {
  const fallback = `HTTP ${status}`;
  if (typeof detail === "string") return { message: detail };
  if (Array.isArray(detail)) {
    const first = detail.find((e): e is ValidationEntry => !!e && typeof e === "object");
    if (!first) return { message: fallback };
    const loc = Array.isArray(first.loc) ? first.loc : [];
    const named = loc.filter(
      (part): part is string => typeof part === "string" && part !== "body",
    );
    // "Value error, Street cannot be blank." — Pydantic's prefix is noise to a
    // user. A bare "Field required" is not a sentence either, so it gets copy.
    const raw = typeof first.msg === "string" ? first.msg : "";
    const message = raw.startsWith("Value error, ")
      ? raw.slice("Value error, ".length)
      : raw && first.type !== "missing"
        ? raw
        : "Please check the highlighted field and try again.";
    return { message, field: named[named.length - 1] };
  }
  if (detail && typeof detail === "object") {
    const obj = detail as { code?: string; reason?: string; field?: string };
    return { message: obj.reason ?? obj.code ?? fallback, field: obj.field };
  }
  return { message: fallback };
}

/** The full error, field included. A Response body can only be read once, so
 *  each call site picks either this or `readDetail` — never both. */
async function readAuthError(res: Response): Promise<AuthError> {
  let parsed: { message: string; field?: string } = {
    message: `HTTP ${res.status}`,
  };
  let payload: Record<string, unknown> | undefined;
  try {
    const body = (await res.json()) as { detail?: unknown };
    parsed = parseErrorBody(body.detail, res.status);
    if (body.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
      payload = body.detail as Record<string, unknown>;
    }
  } catch {
    /* non-JSON body — keep the status fallback */
  }
  return new AuthError(parsed.message, res.status, parsed.field, payload);
}

/** Message only — what the 16 call sites that don't route by field use. */
async function readDetail(res: Response): Promise<string> {
  return (await readAuthError(res)).message;
}
